import {
    fetchChurchInvitations,
    fetchChurchInfo,
    fetchDistrictInvitations,
    fetchDistrictInfo,
    fetchNationInvitations,
    fetchNationInfo,
    fetchDepartments,
    fetchRoles,
    createInvitation,
    cancelInvitation,
    resendInvitation
} from "@features/admin/invitations/api/invitationsApi";
import { fetchMyInvitations, acceptInvitation, rejectInvitation } from "@features/user/invitations/api/myInvitationsApi";
import type { FetchMyInvitationsParams } from "@features/user/invitations/api/myInvitationsApi";
import { InvitationResponse } from "@features/invitations/types/invitation";
import { DepartmentResponse } from "@features/invitations/types/departmentResponse";
import { RoleResponse } from "@features/invitations/types/roleResponse";
import ApiResponse from "@core/types/apiResponse";
import { SendInvitationRequest } from "@features/invitations/types/sendInvitationRequest";
import { EntityResponse } from "@features/invitations/types/entity";


/**
 * Asynchronously fetches pending church invitations.
 *
 * @param data - The request payload containing parameters for fetching invitations.
 * @returns A promise that resolves to an `ApiResponse` containing `InvitationResponse` data.
 */
export const fetchChurchInvitationsAsync = async (data: any): Promise<ApiResponse<InvitationResponse>> => {
    const response = await fetchChurchInvitations(data);
    return response;
};

/**
 * Asynchronously fetches pending district invitations.
 *
 * @param data - The request payload containing parameters for fetching invitations.
 * @returns A promise that resolves to an `ApiResponse` containing `InvitationResponse` data.
 */
export const fetchDistrictInvitationsAsync = async (data: any): Promise<ApiResponse<InvitationResponse>> => {
    const response = await fetchDistrictInvitations(data);
    return response;
};

/**
 * Asynchronously fetches pending nation invitations.
 *
 * @param data - The request payload containing parameters for fetching invitations.
 * @returns A promise that resolves to an `ApiResponse` containing `InvitationResponse` data.
 */
export const fetchNationInvitationsAsync = async (data: any): Promise<ApiResponse<InvitationResponse>> => {
    const response = await fetchNationInvitations(data);
    return response;
};

/**
 * Asynchronously fetches church information based on the provided parameters.
 *
 * @param params - The parameters used to query church information.
 * @returns A promise that resolves to an `ApiResponse` containing an `EntityResponse`.
 */
export const fetchChurchInfoAsync = async (params: any): Promise<ApiResponse<EntityResponse>> => {
    const response = await fetchChurchInfo(params);
    return response;
};

/**
 * Asynchronously fetches district information based on the provided parameters.
 *
 * @param params - The parameters to use when fetching district information.
 * @returns A promise that resolves to an `ApiResponse` containing an `EntityResponse`.
 */
export const fetchDistrictInfoAsync = async (params: any): Promise<ApiResponse<EntityResponse>> => {
    const response = await fetchDistrictInfo(params);
    return response;
};

/**
 * Asynchronously fetches nation information based on the provided parameters.
 *
 * @param params - The parameters used to query nation information.
 * @returns A promise that resolves to an `ApiResponse` containing an `EntityResponse`.
 */
export const fetchNationInfoAsync = async (params: any): Promise<ApiResponse<EntityResponse>> => {
    const response = await fetchNationInfo(params);
    return response;
};

/**
 * Asynchronously fetches a list of departments based on the provided parameters.
 *
 * @param params - The parameters to filter or paginate the department results.
 * @returns A promise that resolves to an `ApiResponse` containing `DepartmentResponse` data.
 */
export const fetchDepartmentsAsync = async (params: any): Promise<ApiResponse<DepartmentResponse>> => {
    const response = await fetchDepartments(params);
    return response;
};

/**
 * Asynchronously fetches roles based on the provided parameters.
 *
 * @param params - The parameters to filter or query roles.
 * @returns A promise that resolves to an ApiResponse containing a RoleResponse.
 */
export const fetchRolesAsync = async (params: any): Promise<ApiResponse<RoleResponse>> => {
    const response = await fetchRoles(params);
    return response;
};

/**
 * Asynchronously fetches the current user's invitations based on the provided parameters.
 *
 * @param params - The parameters to filter or paginate the invitations request.
 * @returns A promise that resolves to an `ApiResponse` containing the invitation response data.
 */
export const fetchMyInvitationsAsync = async (
    params?: FetchMyInvitationsParams
): Promise<ApiResponse<InvitationResponse>> => {
    const response = await fetchMyInvitations(params);
    return response;
};

/**
 * Asynchronously creates an invitation using the provided data.
 *
 * @param data - The invitation request payload conforming to `SendInvitationRequest`.
 * @returns A promise that resolves to an `ApiResponse` containing the result of the invitation creation.
 */
export const createInvitationAsync = async (
    scope: string,
    scopeRefId: number,
    data: SendInvitationRequest
): Promise<ApiResponse<any>> => {
    const response = await createInvitation(scope, scopeRefId, data);
    return response;
};

/**
 * Cancels an invitation asynchronously.
 *
 * @param id - The unique identifier of the invitation to cancel.
 * @param data - An object containing the scope and scope reference ID for the cancellation.
 * @param data.scope - The scope in which the invitation exists.
 * @param data.scope_ref_id - The reference ID for the scope.
 * @returns A promise that resolves to an ApiResponse containing the result of the cancellation.
 */
export const cancelInvitationAsync = async (id: number, data: { scope: string; scope_ref_id: number }): Promise<ApiResponse<any>> => {
    const response = await cancelInvitation(id, data);
    return response;
};

/**
 * Asynchronously resends an invitation with the specified ID and data.
 *
 * @param id - The unique identifier of the invitation to resend.
 * @param data - An object containing the scope and scope reference ID for the invitation.
 * @param data.scope - The scope of the invitation (e.g., user, group).
 * @param data.scope_ref_id - The reference ID associated with the scope.
 * @returns A promise that resolves to an ApiResponse containing the result of the resend operation.
 */
export const resendInvitationAsync = async (id: number, data: { scope: string; scope_ref_id: number }): Promise<ApiResponse<any>> => {
    const response = await resendInvitation(id, data);
    return response;
};

/**
 * Asynchronously accepts an invitation by its ID.
 *
 * @param id - The unique identifier of the invitation to accept.
 * @returns A promise that resolves to an ApiResponse containing the result of the acceptance operation.
 */
export const acceptInvitationAsync = async (id: number): Promise<ApiResponse<any>> => {
    const response = await acceptInvitation(id);
    return response;
};

/**
 * Asynchronously rejects an invitation by its ID.
 *
 * @param id - The unique identifier of the invitation to reject.
 * @returns A promise that resolves to an ApiResponse containing the result of the rejection operation.
 */
export const rejectInvitationAsync = async (id: number): Promise<ApiResponse<any>> => {
    const response = await rejectInvitation(id);
    return response;
};